import { useEffect, useRef, useState } from 'react';
import { motion } from 'framer-motion';
import { RotateCw } from 'lucide-react';

/**
 * Bottle360 — inline drag-to-rotate bottle viewer (uses two photos + rotateY).
 * Renders the bottle on a defined "pedestal card" so the photo always reads
 * against the section background.
 */
export default function Bottle360({
  front,
  back,
  top,
  frontCrop = { size: '135% auto', position: '28% 55%' },
  backCrop = { size: '110% auto', position: 'center 55%' },
  pedestal = '#1B1B1B',
  accent = '#CBBBA0',
  hintColor = 'rgba(250,248,244,0.6)',
  className = '',
  testId,
}) {
  const [rotation, setRotation] = useState(0);
  const [interacted, setInteracted] = useState(false);
  const drag = useRef({ active: false, lastX: 0 });

  useEffect(() => {
    let raf;
    let last = performance.now();
    const tick = (now) => {
      const dt = (now - last) / 1000;
      last = now;
      if (!drag.current.active && !interacted) {
        setRotation((r) => (r + dt * 14) % 360);
      }
      raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [interacted]);

  function onPointerDown(e) {
    drag.current = { active: true, lastX: e.clientX ?? 0 };
    setInteracted(true);
    e.currentTarget.setPointerCapture?.(e.pointerId);
  }
  function onPointerMove(e) {
    if (!drag.current.active) return;
    const x = e.clientX ?? 0;
    const dx = x - drag.current.lastX;
    drag.current.lastX = x;
    setRotation((r) => (r + dx * 0.6 + 360) % 360);
  }
  function onPointerUp() {
    drag.current.active = false;
  }

  const shade = 0.85 + 0.15 * Math.abs(Math.cos((rotation * Math.PI) / 180));

  return (
    <div
      data-testid={testId}
      className={`relative select-none touch-none ${className}`}
      style={{ perspective: 1400 }}
    >
      {/* Pedestal card — defined surface that frames the bottle */}
      <div
        aria-hidden
        className="absolute inset-0 rounded-[28px] overflow-hidden"
        style={{
          backgroundColor: pedestal,
          backgroundImage:
            'radial-gradient(120% 70% at 50% 0%, rgba(255,255,255,0.10) 0%, rgba(255,255,255,0) 55%), radial-gradient(80% 50% at 50% 100%, rgba(0,0,0,0.55) 0%, rgba(0,0,0,0) 60%)',
          boxShadow:
            'inset 0 1px 0 rgba(255,255,255,0.06), 0 40px 80px -30px rgba(0,0,0,0.7)',
        }}
      />

      {/* Faint accent rim */}
      <div
        aria-hidden
        className="absolute inset-0 rounded-[28px] pointer-events-none"
        style={{ border: '1px solid rgba(203,187,160,0.12)' }}
      />

      <div
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={onPointerUp}
        onPointerCancel={onPointerUp}
        onPointerLeave={onPointerUp}
        className="absolute inset-0 cursor-grab active:cursor-grabbing"
      >
        <motion.div
          className="absolute inset-0"
          style={{
            transformStyle: 'preserve-3d',
            transform: `rotateY(${rotation}deg)`,
          }}
        >
          {/* Front face */}
          <div
            className="absolute inset-0 bg-no-repeat rounded-[28px]"
            style={{
              backgroundImage: `url(${front})`,
              backgroundSize: frontCrop.size,
              backgroundPosition: frontCrop.position,
              backfaceVisibility: 'hidden',
              WebkitBackfaceVisibility: 'hidden',
              filter: `brightness(${shade}) contrast(1.08)`,
            }}
          />
          {/* Back face */}
          <div
            className="absolute inset-0 bg-no-repeat rounded-[28px]"
            style={{
              backgroundImage: `url(${back})`,
              backgroundSize: backCrop.size,
              backgroundPosition: backCrop.position,
              backfaceVisibility: 'hidden',
              WebkitBackfaceVisibility: 'hidden',
              transform: 'rotateY(180deg)',
              filter: `brightness(${shade}) contrast(1.08)`,
            }}
          />
        </motion.div>
      </div>

      {/* Top view inset */}
      {top && (
        <motion.div
          initial={{ opacity: 0, x: 16 }}
          whileInView={{ opacity: 1, x: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8, delay: 0.4 }}
          className="absolute top-4 right-4 z-10"
        >
          <div
            className="text-[9px] tracking-[0.3em] uppercase mb-2 text-right"
            style={{ color: hintColor }}
          >
            Top View
          </div>
          <div
            className="w-20 h-20 rounded-xl bg-no-repeat bg-cover bg-center border border-white/15"
            style={{ backgroundImage: `url(${top})` }}
          />
        </motion.div>
      )}

      {/* Drag hint */}
      <motion.div
        initial={{ opacity: 0, y: 6 }}
        animate={{ opacity: interacted ? 0 : 1, y: 0 }}
        transition={{ duration: 0.6 }}
        className="absolute bottom-4 left-1/2 -translate-x-1/2 inline-flex items-center gap-2 text-[10px] tracking-[0.3em] uppercase z-10 pointer-events-none px-3 py-1.5 rounded-full"
        style={{ color: hintColor, backgroundColor: 'rgba(0,0,0,0.45)' }}
      >
        <RotateCw size={11} strokeWidth={1.4} style={{ color: accent }} />
        Drag to Rotate · 360°
      </motion.div>
    </div>
  );
}
